import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { ArrowRight, Mail, Phone } from 'lucide-react';
import AuthShell from '../../components/auth/AuthShell';
import {
  AuthHeading,
  AuthField,
  AuthInput,
  PasswordInput,
  PasswordStrength,
  ErrorBanner,
  CheckBox,
  PrimaryButton,
} from '../../components/auth/AuthPrimitives';
import api from '../../utils/axios';

// ── Step 1 of 2 · Account ─────────────────────────────────────────────────────
//
// Faithful port of the prototype SignupScreen.jsx (Meduri brand). Collects the
// individual login + BAA, registers the user (register returns JWT tokens — no
// follow-up login call needed), fires onLogin so the app is authenticated, then
// routes to the Organization step at /signup/organization.
//
// The email doubles as the username (the backend requires a unique username and
// accepts email-as-username); baa_signed_name is the entered full name.

export default function SignupPage({ onLogin }) {
  const navigate = useNavigate();
  const [data, setData] = useState({
    name: '',
    email: '',
    password: '',
    confirm: '',
    phone: '',
    agree: false,
  });
  const [errors, setErrors] = useState({});
  const [bannerError, setBannerError] = useState('');
  const [loading, setLoading] = useState(false);
  const set = (k, v) => setData((d) => ({ ...d, [k]: v }));

  function validate() {
    const e = {};
    if (!data.name.trim()) e.name = 'Enter your full name';
    if (!data.email) e.email = 'Enter your work email';
    else if (!/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(data.email)) e.email = 'Use a valid email address';
    if (!data.password) e.password = 'Create a password';
    else if (data.password.length < 8) e.password = 'At least 8 characters';
    if (!data.confirm) e.confirm = 'Confirm your password';
    else if (data.confirm !== data.password) e.confirm = "Password fields didn't match.";
    if (!data.agree) e.agree = 'Required to continue';
    return e;
  }

  async function handleSubmit(ev) {
    ev.preventDefault();
    const errs = validate();
    setErrors(errs);
    if (Object.keys(errs).length) return;
    setLoading(true);
    setBannerError('');
    try {
      const payload = {
        username: data.email,
        email: data.email,
        password: data.password,
        password2: data.confirm,
        baa_accepted: true,
        baa_signed_name: data.name,
      };
      const { data: res } = await api.post('/accounts/auth/register/', payload);
      // Register returns JWT tokens directly — no follow-up login needed.
      localStorage.setItem('token', res.tokens.access); // eslint-disable-line no-restricted-properties
      localStorage.setItem('refreshToken', res.tokens.refresh); // eslint-disable-line no-restricted-properties
      onLogin?.();
      navigate('/signup/organization');
    } catch (err) {
      const serverErr = err.response?.data;
      if (serverErr && typeof serverErr === 'object') {
        const first = (k) => (Array.isArray(serverErr[k]) ? serverErr[k][0] : serverErr[k]);
        const mapped = {};
        // username errors surface against the email field (email is the username).
        if (serverErr.username) mapped.email = 'An account with this email already exists.';
        if (serverErr.email) mapped.email = first('email');
        if (serverErr.password) mapped.password = first('password');
        if (serverErr.password2) mapped.confirm = first('password2');
        if (Object.keys(mapped).length) {
          setErrors(mapped);
        } else {
          setBannerError(serverErr.error || serverErr.detail || 'Registration failed. Please try again.');
        }
      } else {
        setBannerError('Registration failed. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  }

  return (
    <AuthShell onLogoClick={() => navigate('/login')} wide>
      <form onSubmit={handleSubmit} noValidate>
        <AuthHeading
          eyebrow="Step 1 of 2 · Account"
          title="Create your Meduri account"
          subtitle="Start with your personal login. Next you’ll set up or join your organization."
        />
        <ErrorBanner>{bannerError}</ErrorBanner>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16, columnGap: 20 }}>
          <AuthField label="Full name" htmlFor="su-name" error={errors.name}>
            <AuthInput id="su-name" autoComplete="name" placeholder="Dr. Avery Chen"
              value={data.name} onChange={(e) => set('name', e.target.value)} error={errors.name} />
          </AuthField>
          <AuthField label="Work email" htmlFor="su-email" error={errors.email}>
            <AuthInput id="su-email" type="email" autoComplete="email" placeholder="avery@clinic.com" leadingIcon={Mail}
              value={data.email} onChange={(e) => set('email', e.target.value)} error={errors.email} />
          </AuthField>
          <AuthField label="Password" htmlFor="su-pw" error={errors.password} hint={!errors.password ? 'At least 8 characters.' : null}>
            <PasswordInput id="su-pw" autoComplete="new-password" value={data.password}
              onChange={(e) => set('password', e.target.value)} error={errors.password} />
            <PasswordStrength value={data.password} />
          </AuthField>
          <AuthField label="Confirm password" htmlFor="su-cpw" error={errors.confirm}>
            <PasswordInput id="su-cpw" autoComplete="new-password" value={data.confirm}
              onChange={(e) => set('confirm', e.target.value)} error={errors.confirm} />
          </AuthField>
          <AuthField label="Phone" htmlFor="su-phone" optional>
            <AuthInput id="su-phone" type="tel" placeholder="(555) 123-4567" leadingIcon={Phone}
              value={data.phone} onChange={(e) => set('phone', e.target.value)} />
          </AuthField>
        </div>

        <div style={{ marginTop: 24, padding: 16, border: '0.4px solid #000', background: '#F8F9FC' }}>
          <CheckBox
            checked={data.agree}
            onChange={(v) => set('agree', v)}
            error={errors.agree}
            label={<>
              I agree to Meduri&rsquo;s{' '}
              <Link to="/legal/baa" style={{ color: '#000', textDecoration: 'underline' }}>Business Associate Agreement</Link>,{' '}
              <Link to="/legal/terms" style={{ color: '#000', textDecoration: 'underline' }}>Terms of Service</Link>, and{' '}
              <Link to="/legal/privacy" style={{ color: '#000', textDecoration: 'underline' }}>Privacy Policy</Link>.
              I confirm I have authority to bind my practice for HIPAA purposes.
            </>}
          />
          {errors.agree && <div style={{ marginTop: 8, fontSize: 12, color: '#FF0004' }}>You must accept the BAA to continue.</div>}
        </div>

        <div style={{ marginTop: 24 }}>
          <PrimaryButton type="submit" loading={loading} icon={ArrowRight}>Continue to Organization</PrimaryButton>
        </div>

        <div style={{ marginTop: 20, paddingTop: 20, borderTop: '0.4px solid #000', fontSize: 14, color: '#1D2433', textAlign: 'center' }}>
          Already have an account?{' '}
          <a onClick={() => navigate('/login')} style={{ color: '#000', textDecoration: 'underline', cursor: 'pointer', fontWeight: 600 }}>Sign in</a>
        </div>
      </form>
    </AuthShell>
  );
}
